import { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatchType } from '../../../store';
import { sendMessage } from '../../../store/globalMessages/actions';
import {
  useAppCrmEventBriefsRetrieveQuery,
  useAppCrmEventBriefsPartialUpdateMutation,
  useAppCrmEventBriefsCreateMutation
} from '../../../store/cabinetApi/generated/crm';
import { skipToken } from '@reduxjs/toolkit/query';
import { EventBrief } from '../../../store/cabinetApi/generated/crm';
import MeetingBriefModal from './MeetingBriefModal';

export interface MeetingBriefModalContainerProps {
  open: boolean;
  onClose: () => void;
  activeBrief: { eventUniqueId: string; generate?: boolean; leaderId?: number; calendarId?: number } | null;
  meetingTitle: string;
  meetingTime?: string;
  meetingDate?: string;
}

export const MeetingBriefModalContainer = ({
  open,
  onClose,
  activeBrief,
  meetingTitle,
  meetingTime,
  meetingDate
}: MeetingBriefModalContainerProps): ReactElement => {
  const dispatch = useDispatch<ThunkDispatchType>();

  const {
    data: briefData,
    isLoading: isLoadingBrief,
  } = useAppCrmEventBriefsRetrieveQuery(activeBrief?.eventUniqueId ? activeBrief.eventUniqueId : skipToken);
  
  const [updateBrief, { isLoading: isUpdatingBrief }] = useAppCrmEventBriefsPartialUpdateMutation();
  const [createBrief/*, { isLoading: isCreatingBrief }*/] = useAppCrmEventBriefsCreateMutation();
  
  // DEMO ONLY: should use the isCreatingBrief state above
  const [isCreatingBrief, setIsCreatingBrief] = useState(false);

  // Handle generate brief flag
  useEffect(() => {
    if (activeBrief?.generate && activeBrief.eventUniqueId && activeBrief.leaderId && activeBrief.calendarId) {
      setIsCreatingBrief(true);
      createBrief({
        event_unique_id: activeBrief.eventUniqueId,
        leader_id: activeBrief.leaderId,
        calendar_pk: activeBrief.calendarId,
      }).unwrap()
        .catch((error) => {
          console.error('Failed to create brief:', error);
        })
        .finally(() => {
          setTimeout(() => {
            setIsCreatingBrief(false);
          }, 3000);
        });
    }
  }, [activeBrief, createBrief]);

  const handleUpdateBrief = async (updatedBrief: Partial<EventBrief>) => {
    if (!briefData || !activeBrief?.eventUniqueId) return;
    
    try {
      await updateBrief({
        eventUniqueId: activeBrief.eventUniqueId,
        patchedEventBrief: updatedBrief
      }).unwrap();
    } catch (error) {
      console.error('Failed to update brief:', error);
      dispatch(sendMessage({
        timeout: 3000,
        message: 'Failed to update meeting brief',
        header: "",
        position: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        active: true,
        severity: 'error',
        autoDismiss: true
      }));
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <MeetingBriefModal
      open={open}
      onClose={handleClose}
      briefData={briefData}
      onUpdate={handleUpdateBrief}
      isUpdating={isUpdatingBrief || isCreatingBrief}
      meetingTitle={meetingTitle}
      meetingTime={meetingTime}
      meetingDate={meetingDate}
      isLoading={isLoadingBrief || isCreatingBrief}
    />
  );
};

export default MeetingBriefModalContainer; 